/**
 * Warranty-claim policy + display constants (stories 101871–101874). Exported via the
 * barrel (ADR-0009: feature-specific constants live inside the feature).
 */
import type { ChipIntent } from '@/components/common/chip';

import type { RejectionReason, WarrantyClaimStatus, WarrantyReason } from './types';

/** Reason dropdown options, in Figma order. */
export const WARRANTY_REASONS: Array<{ value: WarrantyReason; label: string }> = [
  { value: 'not_powering_on', label: 'Not powering on' },
  { value: 'sensor_malfunction', label: 'Sensor malfunction' },
  { value: 'connectivity_issue', label: 'Connectivity issue' },
  { value: 'physical_damage', label: 'Physical damage' },
  { value: 'other', label: 'Other' },
];

/** Human label for a stored reason (detail drawer / listing). */
export const REASON_LABELS: Record<WarrantyReason, string> = {
  not_powering_on: 'Not powering on',
  sensor_malfunction: 'Sensor malfunction',
  connectivity_issue: 'Connectivity issue',
  physical_damage: 'Physical damage',
  other: 'Other',
};

/** When reason === 'other', the custom field is mandatory, 10–500 chars (101871). */
export const REASON_OTHER_MIN = 10;
export const REASON_OTHER_MAX = 500;

/** Description must be between 20 and 2000 chars (101871). */
export const DESC_MIN = 20;
export const DESC_MAX = 2000;

/**
 * Photos: at least one required, capped at 3, JPG/PNG only, 5 MB each (101871). Note the
 * Figma dropzone copy says "up to 5" — the AC governs (max 3), so the helper reflects 3.
 */
export const MIN_PHOTOS = 1;
export const MAX_PHOTOS = 3;
export const MAX_IMAGE_MB = 5;
export const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png';

/** Info-Request follow-up bounds (story 101874). */
export const RESPONSE_MIN = 1;
export const RESPONSE_MAX = 2000;

/**
 * Status → display label + chip colorway. Info Requested uses the red "attention"
 * colorway to pull the grower toward the response they owe; Closed is neutral grey.
 */
export const WARRANTY_STATUS_META: Record<
  WarrantyClaimStatus,
  { label: string; intent: ChipIntent }
> = {
  submitted: { label: 'Submitted', intent: 'review' },
  under_review: { label: 'Under Review', intent: 'wip' },
  info_requested: { label: 'Info Requested', intent: 'attention' },
  approved: { label: 'Approved', intent: 'approved' },
  rejected: { label: 'Rejected', intent: 'published' },
  closed: { label: 'Closed', intent: 'neutral' },
};

/** Filter panel status options, in lifecycle order (story 101872). */
export const WARRANTY_STATUSES: WarrantyClaimStatus[] = [
  'submitted',
  'under_review',
  'info_requested',
  'approved',
  'rejected',
  'closed',
];

/** Statuses that count as an "open" claim — these block a new claim on the device. */
export const OPEN_CLAIM_STATUSES: WarrantyClaimStatus[] = [
  'submitted',
  'under_review',
  'info_requested',
  'approved',
];

/** Rejection reason labels (story 101874). */
export const REJECTION_REASONS: Array<{ value: RejectionReason; label: string }> = [
  { value: 'out_of_warranty', label: 'Out of warranty' },
  { value: 'damage_not_covered', label: 'Damage not covered' },
  { value: 'customer_fault', label: 'Customer fault' },
  { value: 'insufficient_evidence', label: 'Insufficient evidence' },
  { value: 'other', label: 'Other' },
];

export const REJECTION_REASON_LABELS: Record<RejectionReason, string> = {
  out_of_warranty: 'Out of warranty',
  damage_not_covered: 'Damage not covered',
  customer_fault: 'Customer fault',
  insufficient_evidence: 'Insufficient evidence',
  other: 'Other',
};

/** Backend error code when a server-side eligibility check fails at submit (101871). */
export const WARRANTY_ERROR_CODES = {
  notEligible: 'WARRANTY_NOT_ELIGIBLE',
} as const;
